import React, { useState, useEffect, Fragment } from "react";
import { useTranslation } from 'react-i18next';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { Row, Col, Card, CardBody, Button, Badge, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import { useNavigate } from "react-router-dom";
import moment from 'moment';

import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import "../../assets/custom css/loadoverlay.scss";

import Alerts from "../../components/Alerts/alerts";
import DataTable from 'react-data-table-component';
import NotificationService from "../../services/notification.service";
import AuthService from "../Authentication/auth.service";
import { handleNotificationClick as redirectNotification } from "../../helpers/notificationRedirect";

const Notifications = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [pageLimit, setPageLimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Filters
  const [readFilter, setReadFilter] = useState('all'); // 'all', 'read', 'unread'
  const [timeFilter, setTimeFilter] = useState('all'); // 'all', 'today', 'week', 'month'
  const [readFilterDropdown, setReadFilterDropdown] = useState(false);
  const [timeFilterDropdown, setTimeFilterDropdown] = useState(false);

  useEffect(() => {
    fetchNotifications(0, pageLimit);
  }, [pageLimit, readFilter, timeFilter]);

  const getTimeFilterDate = () => {
    const now = new Date();
    switch (timeFilter) {
      case 'today':
        return moment(now).startOf('day').toISOString();
      case 'week':
        return moment(now).subtract(7, 'days').startOf('day').toISOString();
      case 'month':
        return moment(now).subtract(30, 'days').startOf('day').toISOString();
      default:
        return null;
    }
  };

  const fetchNotifications = (offset, limit) => {
    setLoading(true);
    
    // Build filter object
    const filter = {};
    
    // Read/unread filter
    if (readFilter === 'read') {
      filter.read = true;
    } else if (readFilter === 'unread') {
      filter.read = false;
    }
    // 'all' means no filter on read status
    
    // Time filter
    const timeFilterDate = getTimeFilterDate();
    if (timeFilterDate) {
      filter.created_at = { $gte: timeFilterDate };
    }

    NotificationService.getNotifications({
      offset: offset,
      limit: limit,
      sort: { _id: -1 }, // Latest first
      filter: Object.keys(filter).length > 0 ? filter : undefined
    }).then((response) => {
      setLoading(false);
      if (response.data.message === "Unauthorized") {
        AuthService.logout();
        navigate("/login");
        window.location.reload();
      } else if (response.data.status && response.data.data) {
        setNotifications(response.data.data);
        if (response.data.count && response.data.count.count !== undefined) {
          setTotalRows(response.data.count.count);
        } else {
          setTotalRows(response.data.data.length);
        }
      }
    }).catch((error) => {
      setLoading(false);
      const errContent = (error.response && error.response.data) || error.message || error.toString();
      console.error("Error fetching notifications:", errContent);
      Alerts.swalErrorAlert(errContent || "Server Error, Please try again later.");
    });
  };

  const handlePageChange = (page) => {
    const offset = (page - 1) * pageLimit;
    setCurrentPage(page);
    fetchNotifications(offset, pageLimit);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    setPageLimit(newPerPage);
    const offset = (page - 1) * newPerPage;
    setCurrentPage(page);
    fetchNotifications(offset, newPerPage);
  };

  const handleMarkAsRead = (notificationId, showSuccess = false) => {
    NotificationService.markAsRead(notificationId)
      .then((response) => {
        if (response.data.status) {
          // Only show success if explicitly requested (e.g., when clicking mark as read button)
          if (showSuccess) {
            Alerts.swalSuccessAlert("Notification marked as read");
          }
          // Refresh current page
          const offset = (currentPage - 1) * pageLimit;
          fetchNotifications(offset, pageLimit);
        } else {
          Alerts.swalErrorAlert(response.data.message || "Failed to mark as read");
        }
      })
      .catch((error) => {
        console.error('Error marking notification as read:', error);
        Alerts.swalErrorAlert("Failed to mark notification as read");
      });
  };

  const handleDelete = (notificationId) => {
    let msg = t("Once deleted, you will not be able to recover this notification!");
    Alerts.swalConfirmDeleteAlert(msg).then((res) => {
      if (res === true) {
        NotificationService.deleteNotification(notificationId)
          .then((response) => {
            if (response.data.status) {
              Alerts.swalSuccessAlert("Notification deleted successfully");
              // Refresh current page
              const offset = (currentPage - 1) * pageLimit;
              fetchNotifications(offset, pageLimit);
            } else {
              Alerts.swalErrorAlert(response.data.message || "Failed to delete notification");
            }
          })
          .catch((error) => {
            console.error('Error deleting notification:', error);
            Alerts.swalErrorAlert("Failed to delete notification");
          });
      }
    });
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return moment(date).format('DD-MM-YYYY HH:mm');
  };

  const getRelativeTime = (date) => {
    if (!date) return 'N/A';
    return moment(date).fromNow();
  };

  const columns = [
    {
      name: t('Title'),
      width: '200px',
      selector: (row) => row.notification?.title || 'Notification',
      sortable: true,
      wrap: true
    },
    {
      name: t('Message'),
      selector: (row) => row.notification?.body || row.data?.message || 'No message',
      wrap: true,
      grow: 2
    },
    {
      name: t('Date'),
      width: '180px',
      cell: (row) => {
        const date = row.data?.generated_date || row.created_at || row._id?.getTimestamp?.();
        return (
          <div>
            <div>{formatDate(date)}</div>
            <small className="text-muted">{getRelativeTime(date)}</small>
          </div>
        );
      },
      sortable: true
    },
    {
      name: t('Status'),
      width: '100px',
      cell: (row) => {
        return (
          <div>
            {!row.read ? (
              <Badge color="primary" className="me-2">New</Badge>
            ) : (
              <Badge color="secondary" className="me-2">Read</Badge>
            )}
          </div>
        );
      }
    },
    {
      name: t('Actions'),
      width: '120px',
      cell: (row) => {
        return (
          <div onClick={(e) => e.stopPropagation()}>
            {!row.read && (
              <a
                onClick={() => handleMarkAsRead(row._id, true)}
                style={{ cursor: "pointer" }}
                className="EditData"
                title="Mark as Read"
              >
                <i className="pe-7s-check icon-gradient bg-arielle-smile btn-lg px-1"> </i>
              </a>
            )}
            <a
              onClick={() => handleDelete(row._id)}
              style={{ cursor: "pointer" }}
              className="DeleteData"
              title="Delete"
            >
              <i className="pe-7s-trash icon-gradient bg-ripe-malin btn-lg px-0"> </i>
            </a>
          </div>
        );
      }
    }
  ];

  return (
    <Fragment>
      <div className="app-main__outer">
        <div className="app-main__inner">
          <TransitionGroup>
            <CSSTransition component="div" className="TabsAnimation" appear={true}
              timeout={1500} enter={false} exit={false}>
              <div>
                <div className="app-page-title">
                  <div className="page-title-wrapper">
                    <div className="page-title-heading fw-bold">
                      <div className="page-title-icon">
                        <i className="pe-7s-bell icon-gradient bg-tempting-azure" />
                      </div>
                      <div>
                        {t('Notifications')}
                        <div className="page-title-subheading">
                          {t('View and manage all your notifications.')}
                        </div>
                      </div>
                    </div>
                    <div className="page-title-actions">
                      <button type="button" onClick={() => navigate(-1)} className="btn btn-outline-success btn-hover-shine me-3">
                        {t('Back')}
                      </button>
                    </div>
                  </div>
                </div>
                <Row>
                  <Col md="12">
                    <Card className="main-card mb-3">
                      <CardBody>
                        <LoadingOverlay tag="div" active={loading}
                          styles={{ overlay: (base) => ({ ...base }) }}
                          spinner={<DNALoader />}>
                          <div className="d-flex justify-content-between align-items-center mb-3" style={{ marginBottom: '15px' }}>
                            <div className="d-flex gap-2">
                              {/* Read/Unread Filter */}
                              <Dropdown isOpen={readFilterDropdown} toggle={() => setReadFilterDropdown(!readFilterDropdown)}>
                                <DropdownToggle caret color="primary">
                                  {readFilter === 'all' ? t('All') : readFilter === 'read' ? t('Read') : t('Unread')}
                                </DropdownToggle>
                                <DropdownMenu>
                                  <DropdownItem onClick={() => setReadFilter('all')}>{t('All')}</DropdownItem>
                                  <DropdownItem onClick={() => setReadFilter('read')}>{t('Read')}</DropdownItem>
                                  <DropdownItem onClick={() => setReadFilter('unread')}>{t('Unread')}</DropdownItem>
                                </DropdownMenu>
                              </Dropdown>

                              {/* Time Filter */}
                              <Dropdown isOpen={timeFilterDropdown} toggle={() => setTimeFilterDropdown(!timeFilterDropdown)}>
                                <DropdownToggle caret color="info">
                                  {timeFilter === 'all' ? t('All Time') : 
                                   timeFilter === 'today' ? t('Today') :
                                   timeFilter === 'week' ? t('Last 7 Days') :
                                   t('Last 30 Days')}
                                </DropdownToggle>
                                <DropdownMenu>
                                  <DropdownItem onClick={() => setTimeFilter('all')}>{t('All Time')}</DropdownItem>
                                  <DropdownItem onClick={() => setTimeFilter('today')}>{t('Today')}</DropdownItem>
                                  <DropdownItem onClick={() => setTimeFilter('week')}>{t('Last 7 Days')}</DropdownItem>
                                  <DropdownItem onClick={() => setTimeFilter('month')}>{t('Last 30 Days')}</DropdownItem>
                                </DropdownMenu>
                              </Dropdown>
                            </div>
                          </div>

                          <DataTable
                            columns={columns}
                            data={notifications}
                            progressPending={loading}
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            paginationDefaultPage={currentPage}
                            onChangePage={handlePageChange}
                            onChangeRowsPerPage={handlePerRowsChange}
                            paginationPerPage={pageLimit}
                            paginationRowsPerPageOptions={[10, 20, 30, 50, 100]}
                            noDataComponent={t('No notifications found')}
                            highlightOnHover
                            pointerOnHover
                            persistTableHead
                            onRowClicked={(row) => {
                              // Handle notification click - redirect and mark as read (silently, no success modal)
                              const handled = redirectNotification(row, navigate, (id) => handleMarkAsRead(id, false));
                              if (!handled) {
                                // If no redirect, just mark as read if unread (silently)
                                if (!row.read) {
                                  handleMarkAsRead(row._id, false);
                                }
                              }
                            }}
                          />
                        </LoadingOverlay>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </div>
            </CSSTransition>
          </TransitionGroup>
        </div>
      </div>
    </Fragment>
  );
};

export default Notifications;

